import React, { useState, useRef } from 'react';
import { Upload, X } from 'lucide-react';

export default function ImageUploader({
  currentImageUrl = '',
  value = '',
  onChange,
  onImageSelected,
  onImageRemoved,
  label = 'Image Asset',
  recommendedSize = 'JPG, PNG, WEBP up to 5MB',
}) {
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl || value || '');
  const fileInputRef = useRef(null);

  const displayUrl = previewUrl || currentImageUrl || value;

  const handleFile = (file) => {
    if (!file) return;

    // Create object URL for instant UI preview
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);

    if (onImageSelected) {
      onImageSelected(file);
    }
    if (onChange) {
      onChange(localUrl);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setPreviewUrl('');
    if (onImageRemoved) {
      onImageRemoved();
    }
    if (onChange) {
      onChange('');
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-bold text-slate-300 uppercase tracking-wider">
          {label}
        </label>
        {recommendedSize && (
          <span className="text-[11px] text-slate-500">{recommendedSize}</span>
        )}
      </div>

      {displayUrl ? (
        <div className="relative group rounded-xl overflow-hidden border border-slate-800 bg-slate-950 max-h-52 flex items-center justify-center">
          <img
            src={displayUrl}
            alt="Preview"
            className="w-full h-44 object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold transition"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="p-1.5 bg-rose-600/80 hover:bg-rose-600 text-white rounded-lg transition"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all flex flex-col items-center justify-center ${
            dragActive
              ? 'border-[#9BB7A3] bg-[#9BB7A3]/10'
              : 'border-slate-800 hover:border-slate-700 bg-slate-900/40 hover:bg-slate-900/70'
          }`}
        >
          <div className="w-10 h-10 rounded-xl bg-slate-800/80 flex items-center justify-center text-slate-400 mb-2">
            <Upload size={18} />
          </div>
          <p className="text-xs font-semibold text-slate-300">
            Click to upload or drag & drop file
          </p>
          <p className="text-[11px] text-slate-500 mt-1">{recommendedSize}</p>
        </div>
      )}

      {/* Direct URL input fallback */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Or paste direct image URL (https://...)"
          value={displayUrl && !displayUrl.startsWith('blob:') ? displayUrl : ''}
          onChange={(e) => {
            const url = e.target.value;
            setPreviewUrl(url);
            if (onChange) onChange(url);
          }}
          className="w-full px-3 py-2 text-xs bg-slate-950 border border-slate-800 rounded-lg text-slate-300 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
        />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/svg+xml,image/gif"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}
